import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronUp, Upload, X, Image as ImageIcon } from 'lucide-react';
import Select from '../pages/ProfilePage/ProfileChoices';
import '../pages/ProfilePage/ProfilePage.css';
import { useConfig } from '../contexts/ConfigContext';
import './UserEditForm.css';

function resolveMediaUrl(path) {
    if (!path) return '';
    if (path.startsWith('/uploads/')) return `${window.location.origin}${path}`;
    return path;
}

function CertificateViewer({ url, title, onClose }) {
    const ref = useRef(null);
    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return undefined;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialog.showModal();
        return () => {
            dialog.close();
            document.body.style.overflow = overflow;
        };
    }, []);

    return createPortal(
        <dialog
            ref={ref}
            className="certificate-viewer-dialog"
            aria-label={title}
            onCancel={(e) => {
                e.preventDefault();
                onClose();
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="certificate-viewer">
                <header>
                    <strong>{title}</strong>
                    <button type="button" autoFocus onClick={onClose} aria-label="Закрыть">
                        Закрыть ×
                    </button>
                </header>
                <img src={url} alt={title} />
                <a href={url} target="_blank" rel="noreferrer">
                    Открыть файл в новой вкладке ↗
                </a>
            </div>
        </dialog>,
        document.body
    );
}

const UserEditForm = ({ initialData, onSubmit, loading }) => {
    const { config } = useConfig();
    const [activeTab,setActiveTab]=useState('general');
    const [validationError,setValidationError]=useState('');
    const isNasledie = config?.code === 'nasledie';
    const normalizeTelegramUsernameForView = (raw) => {
        const v = String(raw || '').trim().replace(/^@+/, '');
        if (!v) return '';
        // Telegram username: латиница/цифры/underscore, минимум 5 символов
        if (!/^[A-Za-z0-9_]{5,}$/.test(v)) return '';
        return v;
    };
    // Загрузка опций для селектов
    const { data: profileOptions, isLoading: optionsLoading, error: optionsError } = useQuery('profileOptions', async () => {
        const response = await axios.get(`${API_BASE_URL}/config/profile-options`);
        return response.data;
    });

    const [formData, setFormData] = useState({
        last_name: '',
        name: '',
        email: '',
        password: '',
        phone: '',
        photo_url: '',
        short_description: '',
        description: '',
        therapist_aggregator_short: '',
        therapist_aggregator_full: '',
        therapist_education: '',
        therapist_inn: '',
        therapist_profile_status: 'editing',
        role: 'user',
        is_teacher: true,
        is_therapist: false,
        is_knowledge_editor: false,
        is_external_staff: false,
        telegram_username: '',
        telegram_chat_id: null,
        city: [],
        language: [],
        therapy_topics: [],
        work_format: [],
        qualification: [],
        price: null,
        topic_prices: {},
        video_card_url: '',
        is_active: true
    });
    const [certificates, setCertificates] = useState([]);
    const [viewingCertificate, setViewingCertificate] = useState(null);
    const [topicPricesExpanded, setTopicPricesExpanded] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingCertificates, setUploadingCertificates] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                last_name: initialData.last_name || '',
                name: initialData.name || '',
                email: initialData.email || '',
                password: '',
                phone: initialData.phone || '',
                photo_url: initialData.photo_url || '',
                short_description: initialData.short_description || '',
                description: initialData.description || '',
                therapist_aggregator_short: initialData.therapist_aggregator_short || '',
                therapist_aggregator_full: initialData.therapist_aggregator_full || '',
                therapist_education: initialData.therapist_education || '',
                therapist_inn: initialData.therapist_inn || '',
                therapist_profile_status: initialData.therapist_profile_status || 'editing',
                role: initialData.role || 'user',
                is_teacher: initialData.is_teacher !== undefined ? initialData.is_teacher : true,
                is_therapist: initialData.is_therapist || false,
                is_knowledge_editor: initialData.is_knowledge_editor || false,
                is_external_staff: Boolean(initialData.is_external_staff),
                telegram_username: initialData.telegram_username || '',
                telegram_chat_id: initialData.telegram_chat_id || null,
                city: initialData.city || [],
                language: initialData.language || [],
                therapy_topics: initialData.therapy_topics || [],
                work_format: initialData.work_format || [],
                qualification: Array.isArray(initialData.qualification)
                    ? initialData.qualification
                    : (initialData.qualification ? [initialData.qualification] : []),
                price: initialData.price || null,
                topic_prices: initialData.topic_prices || {},
                video_card_url: initialData.video_card_url || '',
                is_active: initialData.is_active !== undefined ? initialData.is_active : true
            });
            setCertificates(initialData.certificates || []);
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let nextValue = type === 'checkbox' ? checked : value;
        if (name === 'is_active') {
            nextValue = value === true || value === 'true';
        }
        setFormData(prev => ({
            ...prev,
            [name]: nextValue
        }));
    };

    const handleMultiSelectChange = (name, selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        setFormData(prev => {
            const newData = { ...prev, [name]: values };
            // При изменении тем-запросов, удаляем цены для удаленных тем
            if (name === 'therapy_topics') {
                const topicPrices = { ...prev.topic_prices };
                Object.keys(topicPrices).forEach(topic => {
                    if (!values.includes(topic)) {
                        delete topicPrices[topic];
                    }
                });
                newData.topic_prices = topicPrices;
            }
            return newData;
        });
    };

    const handleTopicPriceChange = (topic, price) => {
        setFormData(prev => ({
            ...prev,
            topic_prices: {
                ...prev.topic_prices,
                [topic]: price
            }
        }));
    };

    const removeTopicPrice = (topic) => {
        setFormData(prev => {
            const newTopicPrices = { ...prev.topic_prices };
            delete newTopicPrices[topic];
            return {
                ...prev,
                topic_prices: newTopicPrices
            };
        });
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Выберите изображение');
            return;
        }

        if (file.size > 30 * 1024 * 1024) {
            toast.error('Размер файла не должен превышать 30MB');
            return;
        }

        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('photo', file);

            // Редактирование: привязываем к пользователю. Создание: только файл, без UPDATE админа.
            const url = initialData?.id
                ? `${API_BASE_URL}/upload/photo?userId=${initialData.id}`
                : `${API_BASE_URL}/upload/photo?defer=1`;

            const response = await axios.post(url, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setFormData(prev => ({ ...prev, photo_url: response.data.photo_url }));
            toast.success('Фото загружено успешно');
        } catch (error) {
            console.error('Ошибка загрузки фото:', error);
            toast.error(error.response?.data?.message || 'Ошибка загрузки фото');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleDeletePhoto = async () => {
        if (!window.confirm('Удалить загруженное фото?')) return;

        try {
            const url = initialData?.id
                ? `${API_BASE_URL}/upload/photo?userId=${initialData.id}`
                : `${API_BASE_URL}/upload/photo?defer=1&photo_url=${encodeURIComponent(formData.photo_url || '')}`;

            await axios.delete(url, { withCredentials: true });
            setFormData(prev => ({ ...prev, photo_url: '' }));
            toast.success('Фото удалено');
        } catch (error) {
            console.error('Ошибка удаления фото:', error);
            toast.error(error.response?.data?.message || 'Ошибка удаления фото');
        }
    };

    const handleCertificatesUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
        if (invalidFiles.length > 0) {
            toast.error('Выберите только изображения');
            return;
        }

        const largeFiles = files.filter(file => file.size > 30 * 1024 * 1024);
        if (largeFiles.length > 0) {
            toast.error('Размер каждого файла не должен превышать 30MB');
            return;
        }

        setUploadingCertificates(true);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('certificates', file));

            const url = initialData?.id
                ? `${API_BASE_URL}/upload/certificates?userId=${initialData.id}`
                : `${API_BASE_URL}/upload/certificates?defer=1`;

            const response = await axios.post(url, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.deferred) {
                setCertificates(prev => [...prev, ...(response.data.certificates || [])]);
            } else {
                setCertificates(response.data.certificates);
            }
            toast.success('Сертификаты загружены успешно');
        } catch (error) {
            console.error('Ошибка загрузки сертификатов:', error);
            toast.error(error.response?.data?.message || 'Ошибка загрузки сертификатов');
        } finally {
            setUploadingCertificates(false);
        }
    };

    const handleDeleteCertificate = async (certificatePath) => {
        if (!window.confirm('Удалить этот сертификат?')) return;

        try {
            if (!initialData?.id) {
                await axios.delete(`${API_BASE_URL}/upload/certificate`, {
                    withCredentials: true,
                    data: {
                        certificatePath,
                        defer: true
                    }
                });
                setCertificates(prev => prev.filter(c => c !== certificatePath));
            } else {
                const response = await axios.delete(`${API_BASE_URL}/upload/certificate`, {
                    withCredentials: true,
                    data: {
                        certificatePath,
                        userId: initialData.id
                    }
                });
                setCertificates(response.data.certificates);
            }
            toast.success('Сертификат удален');
        } catch (error) {
            console.error('Ошибка удаления сертификата:', error);
            toast.error(error.response?.data?.message || 'Ошибка удаления сертификата');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if(!formData.name.trim() || ((!initialData || formData.is_active) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) || (!initialData && !formData.password)){setActiveTab('general');setValidationError('Укажите имя, корректную почту и пароль для нового пользователя.');return;}
        setValidationError('');

        // Очищаем пустые цены перед отправкой
        const cleanedTopicPrices = {};
        Object.keys(formData.topic_prices || {}).forEach(topic => {
            const raw = formData.topic_prices[topic];
            if (raw !== '' && raw !== null && raw !== undefined) {
                const normalized = String(raw).replace(',', '.');
                const n = Number(normalized);
                if (Number.isFinite(n)) {
                    cleanedTopicPrices[topic] = n;
                }
            }
        });

        // Преобразуем пустую строку в null для price
        const submitData = {
            ...formData,
            price: formData.price === '' || formData.price === null ? null : parseFloat(formData.price),
            topic_prices: cleanedTopicPrices,
            certificates: certificates || []
        };

        onSubmit(submitData);
    };

    const handleUnlinkTelegram = async () => {
        if (!initialData?.id) return;
        if (!window.confirm('Отвязать Telegram у этого пользователя?')) return;
        try {
            await axios.post(
                `${API_BASE_URL}/users/${initialData.id}/telegram-unlink`,
                {},
                { withCredentials: true }
            );
            setFormData((prev) => ({
                ...prev,
                telegram_username: '',
                telegram_chat_id: null
            }));
            toast.success('Telegram отвязан');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка отвязки Telegram');
        }
    };

    const toOptions = (arr) => arr ? arr.map(item => ({ value: item, label: item })) : [];
    const getSelectedOptions = (values) => values ? values.map(v => ({ value: v, label: v })) : [];

    const requireEmailValidation = !initialData || formData.is_active;

    return (
        <form onSubmit={handleSubmit} className="user-edit-form profile-page admin-profile-editor">
            <header className="ue-identity">{formData.photo_url?<img src={formData.photo_url} alt="Фото пользователя"/>:<span className="ue-avatar">{(formData.name || 'Н').slice(0,1)}</span>}<div><h2>{[formData.last_name,formData.name].filter(Boolean).join(' ') || 'Новый пользователь'}</h2><p>{formData.email || 'Заполните общие данные'}</p></div></header>
            <nav className="ue-tabs" aria-label="Разделы профиля пользователя">{[['general','Общие данные'],['access','Роли и доступ'],...(formData.is_teacher?[['teacher','Преподаватель']]:[]),...(formData.is_therapist?[['therapist','Терапевт']]:[])].map(([key,label])=><button type="button" key={key} aria-current={activeTab===key?'page':undefined} className={activeTab===key?'active':''} onClick={()=>setActiveTab(key)}>{label}</button>)}</nav>
            {validationError && <p role="alert" className="ue-error">{validationError}</p>}
            {activeTab==='general' && <section className="ue-section"><h3>Общие данные</h3><p className="ue-hint">Имя, контакты и фотография для всех профилей пользователя.</p>
            <div className="form-row">
                <div className="form-group">
                    <label>Фамилия</label>
                    <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Фамилия"
                    />
                </div>
                <div className="form-group">
                    <label>Имя *</label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Имя"
                    />
                </div>

                <div className="form-group">
                    <label>Email *</label>
                    <input
                        type={requireEmailValidation ? 'email' : 'text'}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required={requireEmailValidation}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Пароль {!initialData && '*'}</label>
                    <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required={!initialData}
                        placeholder={initialData ? 'Оставьте пустым для сохранения текущего' : ''}
                    />
                </div>

                <div className="form-group">
                    <label>Телефон</label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div className="form-group">
                <label>Telegram</label>
                {formData.telegram_chat_id ? (
                    <>
                        <p className="form-hint">
                            Подключен
                            {normalizeTelegramUsernameForView(formData.telegram_username)
                                ? `: @${normalizeTelegramUsernameForView(formData.telegram_username)}`
                                : ''}.
                        </p>
                        {initialData?.id && (
                            <button
                                type="button"
                                className="btn-delete-photo"
                                onClick={handleUnlinkTelegram}
                                style={{ width: 'fit-content' }}
                            >
                                Отвязать Telegram
                            </button>
                        )}
                    </>
                ) : (
                    <small className="form-hint">Не подключен</small>
                )}
            </div>

            <div className="form-group">
                <label>Фото профиля</label>
                <div className="photo-upload-section">
                    <div className="photo-upload-controls">
                        <label className="btn-upload-photo">
                            <Upload size={18} />
                            {uploadingPhoto ? 'Загрузка...' : 'Загрузить фото'}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                style={{ display: 'none' }}
                                disabled={uploadingPhoto}
                            />
                        </label>
                        {formData.photo_url && formData.photo_url.startsWith('/uploads/photos/') && (
                            <button
                                type="button"
                                className="btn-delete-photo"
                                onClick={handleDeletePhoto}
                            >
                                <X size={18} />
                                Удалить
                            </button>
                        )}
                    </div>
                    {formData.photo_url && (
                        <div className="photo-preview">
                            <img
                                src={formData.photo_url.startsWith('/uploads/')
                                    ? `${window.location.origin}${formData.photo_url}`
                                    : formData.photo_url}
                                alt="Превью"
                            />
                        </div>
                    )}
                    <div className="photo-url-input">
                        <label>Или укажите ссылку на фото</label>
                        <input
                            type="text"
                            name="photo_url"
                            value={formData.photo_url}
                            onChange={handleChange}
                            placeholder="https://example.com/photo.jpg"
                        />
                    </div>
                </div>
            </div>

            </section>}
            {activeTab==='access' && <section className="ue-section"><h3>Роли и доступ</h3><p className="ue-hint">Профессиональные разделы появляются после включения соответствующей роли.</p>            <div className="form-row">
                <div className="form-group">
                    <label>Роль</label>
                    <select name="role" value={formData.role} onChange={handleChange}>
                        <option value="user">Пользователь</option>
                        <option value="admin">Администратор</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Статус</label>
                    <select name="is_active" value={formData.is_active} onChange={handleChange}>
                        <option value={true}>Активен</option>
                        <option value={false}>Неактивен</option>
                    </select>
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="is_teacher"
                            checked={formData.is_teacher}
                            onChange={handleChange}
                        />
                        <span>Преподаватель (доступ к группам и расписанию)</span>
                    </label>
                </div>

                <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="is_therapist"
                            checked={formData.is_therapist}
                            onChange={handleChange}
                        />
                        <span>Терапевт (личные консультации)</span>
                    </label>
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="is_knowledge_editor"
                            checked={formData.is_knowledge_editor}
                            onChange={handleChange}
                        />
                        <span>Редактор знаний (создание и редактирование статей)</span>
                    </label>
                </div>

                {isNasledie && (
                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="is_external_staff"
                                checked={formData.is_external_staff}
                                onChange={handleChange}
                            />
                            <span>Внештатный</span>
                        </label>
                    </div>
                )}
            </div>



</section>}
            {activeTab==='teacher' && formData.is_teacher && <section className="ue-section"><h3>Профиль преподавателя</h3>            <div className="form-group">
                <label>Краткое описание</label>
                <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    rows="2"
                />
            </div>

            <div className="form-group">
                <label>Полное описание</label>
                <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                />
            </div>

</section>}
            {/* Поля для психологов */}
            {activeTab==='therapist' && optionsLoading && <p>Загрузка настроек…</p>}
            {activeTab==='therapist' && optionsError && <p role="alert">Не удалось загрузить настройки профиля. Обновите страницу.</p>}
            {activeTab==='therapist' && formData.is_therapist && profileOptions && (
                <>
                    <div className="therapist-section">
                        <h3>Профиль терапевта</h3>
                    </div>

                    <div className="form-group">
                        <label>{config?.code === 'mag' ? 'Статус в Магрегаторе' : 'Статус публикации'}</label>
                        <select
                            name="therapist_profile_status"
                            value={formData.therapist_profile_status || 'editing'}
                            onChange={handleChange}
                        >
                            <option value="editing">Редактируется</option>
                            <option value="published">Опубликован</option>
                        </select>
                        <small className="form-hint">
                            После публикации профессиональные данные может изменять только администратор.
                        </small>
                    </div>

                    <div className="form-group">
                        <label>Краткое описание</label>
                        <textarea
                            name="therapist_aggregator_short"
                            value={formData.therapist_aggregator_short}
                            onChange={handleChange}
                            rows="2"
                            maxLength="600"
                            placeholder="Краткий текст для карточки в списке психологов"
                        />
                    </div>

                    <div className="form-group">
                        <label>Полное описание</label>
                        <textarea
                            name="therapist_aggregator_full"
                            value={formData.therapist_aggregator_full}
                            onChange={handleChange}
                            rows="5"
                            placeholder="Подробный текст для блока «О себе» на публичной странице"
                        />
                    </div>

                    <div className="form-group">
                        <label>Образование</label>
                        <textarea
                            name="therapist_education"
                            value={formData.therapist_education}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Образование (под полным описанием на публичной странице)"
                        />
                    </div>

                    <div className="form-group">
                        <label>ИНН (для оплаты / чека, не показывается на публичной странице)</label>
                        <input
                            type="text"
                            name="therapist_inn"
                            value={formData.therapist_inn}
                            onChange={handleChange}
                            placeholder="12 цифр"
                            maxLength={20}
                            autoComplete="off"
                        />
                    </div>

                    <div className="form-group">
                        <label>Города</label>
                        <Select
                            isMulti
                            options={toOptions(profileOptions.cities)}
                            value={getSelectedOptions(formData.city)}
                            onChange={(selected) => handleMultiSelectChange('city', selected)}
                            placeholder="Выберите города..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="form-group">
                        <label>Языки</label>
                        <Select
                            isMulti
                            options={toOptions(profileOptions.languages)}
                            value={getSelectedOptions(formData.language)}
                            onChange={(selected) => handleMultiSelectChange('language', selected)}
                            placeholder="Выберите языки..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="form-group">
                        <label>Темы-запросы</label>
                        <Select
                            isMulti
                            options={toOptions(profileOptions.therapyTopics)}
                            value={getSelectedOptions(formData.therapy_topics)}
                            onChange={(selected) => handleMultiSelectChange('therapy_topics', selected)}
                            placeholder="Выберите темы..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="form-group">
                        <label>Форматы работы</label>
                        <Select
                            isMulti
                            options={toOptions(profileOptions.formats)}
                            value={getSelectedOptions(formData.work_format)}
                            onChange={(selected) => handleMultiSelectChange('work_format', selected)}
                            placeholder="Выберите форматы..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="form-group">
                        <label>Квалификации</label>
                        <Select
                            isMulti
                            options={toOptions(profileOptions.qualifications || [])}
                            value={getSelectedOptions(formData.qualification)}
                            onChange={(selected) => handleMultiSelectChange('qualification', selected)}
                            placeholder="Выберите квалификации..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="form-group">
                        <label>Базовая стоимость консультации (руб.)</label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price || ''}
                            onChange={handleChange}
                            onWheel={(e) => e.target.blur()}
                            placeholder="0"
                            min="0"
                            step="1"
                        />
                        <small className="form-hint">Базовая цена, если не указана цена для конкретной темы</small>
                    </div>

                    {/* Цены по темам-запросам */}
                    {formData.therapy_topics && formData.therapy_topics.length > 0 && (
                        <div className="form-group topic-prices-section">
                            <button
                                type="button"
                                className="topic-prices-header"
                                onClick={() => setTopicPricesExpanded(!topicPricesExpanded)}
                            >
                                <div className="topic-prices-header-content">
                                    <span>Цены по темам-запросам</span>
                                    <span className="topic-prices-count">
                                        ({Object.keys(formData.topic_prices || {}).filter(t => formData.topic_prices[t]).length} из {formData.therapy_topics.length})
                                    </span>
                                </div>
                                {topicPricesExpanded ? (
                                    <ChevronUp size={20} />
                                ) : (
                                    <ChevronDown size={20} />
                                )}
                            </button>

                            <div className={`topic-prices-content ${topicPricesExpanded ? 'expanded' : 'collapsed'}`}>
                                <div className="topic-prices-list">
                                    {formData.therapy_topics.map(topic => {
                                        const topicPrice = formData.topic_prices?.[topic] || '';
                                        return (
                                            <div key={topic} className="topic-price-item">
                                                <label className="topic-price-label">{topic}</label>
                                                <div className="topic-price-input-group">
                                                    <input
                                                        type="number"
                                                        value={topicPrice}
                                                        onChange={(e) => handleTopicPriceChange(topic, e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        placeholder="Базовая цена"
                                                        min="0"
                                                        step="1"
                                                        className="topic-price-input"
                                                    />
                                                    <span className="topic-price-currency">₽</span>
                                                    {topicPrice && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeTopicPrice(topic)}
                                                            className="btn-remove-price"
                                                            title="Удалить цену"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                                {!topicPrice && (
                                                    <small className="topic-price-hint">
                                                        Будет использована базовая цена: {formData.price || 'не указана'} ₽
                                                    </small>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <small className="form-hint">
                                    Укажите отдельную цену для каждой темы. Если цена не указана, будет использована базовая цена.
                                </small>
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Ссылка на видеовизитку</label>
                        <input
                            type="url"
                            name="video_card_url"
                            value={formData.video_card_url}
                            onChange={handleChange}
                            placeholder="https://youtube.com/watch?v=..."
                        />
                    </div>

                    <div className="form-group certificates-section">
                        <label>Сертификаты</label>
                        <div className="certificates-upload-section">
                            <label className="btn-upload-certificates">
                                <ImageIcon size={18} />
                                {uploadingCertificates ? 'Загрузка...' : 'Загрузить сертификаты'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleCertificatesUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploadingCertificates}
                                />
                            </label>
                            <small className="form-hint">Можно загрузить несколько файлов одновременно (до 20 штук, каждый до 30MB). Нажмите на сертификат, чтобы открыть крупно.</small>

                            {certificates && certificates.length > 0 && (
                                <div className="certificates-grid">
                                    {certificates.map((cert, index) => {
                                        const url = resolveMediaUrl(cert);
                                        const title = `Сертификат ${index + 1}`;
                                        return (
                                            <div
                                                key={index}
                                                className="certificate-item"
                                                role="button"
                                                tabIndex={0}
                                                title="Открыть сертификат"
                                                onClick={() => setViewingCertificate({ url, title })}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        setViewingCertificate({ url, title });
                                                    }
                                                }}
                                            >
                                                <img src={url} alt={title} />
                                                <button
                                                    type="button"
                                                    className="btn-delete-certificate"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteCertificate(cert);
                                                    }}
                                                    title="Удалить сертификат"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {viewingCertificate && (
                                <CertificateViewer
                                    url={viewingCertificate.url}
                                    title={viewingCertificate.title}
                                    onClose={() => setViewingCertificate(null)}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
        </form>
    );
};

export default UserEditForm;

